import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import LoadingDots from '../common/LoadingDots';
import { useAppBar } from '../../context/AppBarContext';
import { ReactComponent as XmarkIcon } from '../../icons/xmark.svg';

import './PageBar.scss';

/**
 * The bar's own row: the way out, the title and the actions. PageBar portals it into the app bar;
 * rendered in place, without `back`, it heads a section further down the page in the same style.
 */
export function PageBarHead({ back, onBack, title, subtitle, actions = [], className }) {
  const { t } = useTranslation();

  return (
    <div className={cx('PageBar head', className)}>
      {back &&
        (back.close ? (
          <button type="button" className="close" aria-label={back.label || t('common.close')} onClick={onBack}>
            <Icon icon={<XmarkIcon />} />
          </button>
        ) : (
          <button
            type="button"
            className={cx('back', { 'is-icon': back.icon })}
            aria-label={back.icon ? back.label || t('common.back') : undefined}
            onClick={onBack}
          >
            <span className="back-chevron" aria-hidden="true" />
            {!back.icon && <span>{back.label}</span>}
          </button>
        ))}

      {/* Keyed, so a new title fades in over the old rather than swapping in place */}
      {subtitle ? (
        <span key={`${title}|${subtitle}`} className="title is-stacked">
          <span className="main">{title}</span>
          <span className="sub">{subtitle}</span>
        </span>
      ) : (
        <span key={title} className="title">
          {title}
        </span>
      )}

      {actions.map((action) => (
        <button
          key={action.label}
          type={action.form ? 'submit' : 'button'}
          form={action.form}
          className={cx('action', {
            'is-loading': action.loading,
            'is-on': action.pressed,
            'is-primary': action.form,
          })}
          aria-label={action.label}
          aria-pressed={action.pressed}
          aria-busy={action.loading || undefined}
          disabled={action.disabled || action.loading}
          onClick={action.onClick}
        >
          {action.loading ? (
            <LoadingDots width="1.5em" />
          ) : (
            <Icon icon={action.icon} className={action.iconClassName} />
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * A page's header, portalled into the app bar in place of the logo and menu: a way out on the
 * left, a title, the page's own buttons on the right, and any toggles in a row underneath.
 *
 * `back` is { label, close, icon, onClick, fallback }. `close` draws a round ✕ instead of "‹ label",
 * and `icon` a round ‹ alone, for when the label would only say "Back". Without `onClick` it goes
 * back through history, or to `fallback` when opened from a link. A `subtitle` stacks a smaller
 * second line under the title. `sticky` keeps the bar stuck to the top as the page scrolls.
 * Each of `actions` is { label, icon, iconClassName, onClick, form, disabled, loading, pressed }. `form`
 * makes it that form's submit button, so it can sit up here outside the form, filled as the last step, `loading` swaps the
 * icon for dots, and `pressed` fills it for a toggle that's on. Each of `chips` is
 * { label, icon, iconClassName, pressed, onClick }.
 */
function PageBar({ back, title, subtitle, actions = [], chips = [], sticky = false }) {
  const { pageSlot, traySlot, pageMode, setPageMode, setPageSticky, goBack, markBack, routeMotion } = useAppBar();
  // Fixed at mount. pageMode is still the bar's old state on this first render: from the logo and
  // menu it's always further in, from another page it's whichever way the route went.
  const [motion] = useState(() => routeMotion && (pageMode ? routeMotion : 'forward'));

  // Layout, so the logo is already gone on the first paint
  useLayoutEffect(() => {
    setPageMode(true);

    return () => setPageMode(false);
  }, [setPageMode]);

  useLayoutEffect(() => {
    setPageSticky(sticky);

    return () => setPageSticky(false);
  }, [sticky, setPageSticky]);

  const leave = () => {
    markBack();
    if (back?.onClick) back.onClick();
    else goBack(back?.fallback || '/songs');
  };

  const head = (
    <PageBarHead
      back={back}
      onBack={leave}
      title={title}
      subtitle={subtitle}
      actions={actions}
      className={motion && `enter-${motion}`}
    />
  );

  const row = chips.length > 0 && (
    <div className="PageBar chips">
      {chips.map((chip, index) => (
        <button
          key={index}
          type="button"
          className={cx('chip', { 'is-on': chip.pressed })}
          aria-pressed={chip.pressed}
          onClick={chip.onClick}
        >
          <Icon icon={chip.icon} className={chip.iconClassName} />
          <span>{chip.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      {pageSlot && createPortal(head, pageSlot)}
      {traySlot && row && createPortal(row, traySlot)}
    </>
  );
}

export default PageBar;
